import { useState, useCallback, useRef } from 'react';
import axios from 'axios';

// Usar URL baseada no hostname atual
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const port = window.location.port;
  
  // Em dev (Vite), conecta na porta 5001
  if (port === '5173' || port === '5174') {
    return 'http://localhost:5001';
  }
  
  // Em produção (porta 80/443 via Nginx), sem porta
  return `${window.location.protocol}//${hostname}`;
};

const API_URL = getApiUrl();

console.log('🌐 [CHAT] API URL configurada:', API_URL);

interface Message {
  id: string;
  _id?: string;
  channelId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  userEmail?: string;
  content: string;
  timestamp: Date;
  type: 'text' | 'notification' | 'system';
  mentions?: string[];
  attachments?: Array<{
    name: string;
    url: string;
    type: string;
  }>;
  replyTo?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  edited?: boolean;
  editedAt?: Date;
}

interface Channel {
  id: string;
  name: string;
  type: 'general' | 'flow' | 'department' | 'direct';
  unreadCount?: number;
  flowId?: string;
  departmentId?: string;
  empresaId?: string;
}

interface UseChatReturn {
  channels: Channel[];
  messages: Message[];
  loading: boolean;
  error: string | null;
  loadChannels: (empresaId: string, userId: string) => Promise<void>;
  loadMessages: (channelId: string, options?: { silent?: boolean; userId?: string; empresaId?: string }) => Promise<void>;
  sendMessage: (message: Omit<Message, 'id' | 'timestamp'>) => Promise<void>;
  sendNotification: (channelId: string, content: string, metadata?: any) => Promise<void>;
  refreshMessages: (channelId: string, userId?: string, empresaId?: string) => Promise<void>;
  addExternalMessage: (rawMsg: any) => void;
}

export const useChat = (): UseChatReturn => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastMessagesSignature = useRef<string>('');
  // Último canal cujas mensagens foram pedidas. Como `messages` é um estado só,
  // duas cargas simultâneas (ex.: abrir Geral e uma DM juntos) competiam e a que
  // respondesse por último vencia — a aba certa ficava aberta mostrando o
  // conteúdo do outro canal. Guardamos o pedido corrente para descartar o velho.
  const canalSolicitadoRef = useRef<string | null>(null);

  const normaliseMessage = (msg: any): Message => {
    const rawId = msg.id || msg._id;
    const normalisedId = rawId ? String(rawId) : `${msg.channelId || 'message'}-${msg.timestamp || Date.now()}`;
    const timestampValue = msg.timestamp instanceof Date ? msg.timestamp : new Date(msg.timestamp || Date.now());

    return {
      ...msg,
      id: normalisedId,
      _id: msg._id ? String(msg._id) : normalisedId,
      timestamp: timestampValue
    } as Message;
  };

  const buildSignature = (msgs: Message[]) =>
    msgs
      .map(message => {
        const idPart = message.id ?? 'no-id';
        const timePart = message.timestamp instanceof Date ? message.timestamp.getTime() : 0;
        const contentPart = message.content ?? '';
        const editedPart = message.edited ? '1' : '0';
        const editTimePart = message.editedAt ? new Date(message.editedAt).getTime() : 0;
        return `${idPart}#${timePart}#${contentPart}#${editedPart}#${editTimePart}`;
      })
      .join('|');

  const loadChannels = useCallback(async (empresaId: string, userId: string) => {
    try {
      setLoading(true);
      setError(null);

      console.log('📡 [CHAT] Requisitando canais...', { empresaId, userId, apiUrl: API_URL });

      const response = await axios.get(`${API_URL}/api/chat/channels`, {
        params: { empresaId, userId },
        timeout: 15000 // 15 segundos timeout (reduzido de 30s para detectar problemas mais rápido)
      });

      console.log('📨 [CHAT] Resposta da API:', response.data);

      if (response.data.success) {
        console.log(`✅ [CHAT] ${response.data.data.length} canais carregados`);
        setChannels(response.data.data);
      } else {
        console.error('❌ [CHAT] Erro na resposta:', response.data.error);
        setError(response.data.error || 'Erro ao carregar canais');
        alert(`Erro ao carregar canais: ${response.data.error}`);
      }
    } catch (err: any) {
      console.error('❌ [CHAT] Erro ao carregar canais:', err);
      console.error('📍 [CHAT] URL tentada:', `${API_URL}/api/chat/channels`);
      console.error('📍 [CHAT] Detalhes:', {
        message: err.message,
        code: err.code,
        response: err.response?.data,
        status: err.response?.status
      });
      
      const errorMsg = err.response?.data?.error 
        || err.message 
        || 'Erro ao carregar canais. Verifique se o backend está rodando.';
      
      setError(errorMsg);
      alert(`Erro de conexão: ${errorMsg}\n\nURL: ${API_URL}/api/chat/channels`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMessages = useCallback(async (channelId: string, options: { silent?: boolean; userId?: string; empresaId?: string } = {}) => {
    try {
      if (!options.silent) {
        setLoading(true);
      }
      setError(null);

      console.log('📥 [CHAT] Carregando mensagens do canal:', channelId);
      canalSolicitadoRef.current = channelId;

      const params: any = { channelId, limit: 100 };
      if (options.userId) {
        params.userId = options.userId;
      }
      if (options.empresaId) {
        params.empresaId = options.empresaId;
      }

      const response = await axios.get(`${API_URL}/api/chat/messages`, {
        params,
        timeout: 10000 // 10 segundos timeout para mensagens
      });

      console.log('📨 [CHAT] Mensagens recebidas:', response.data);

      // Resposta de um canal que não é mais o pedido atual: descartar.
      // Sem isso, o Geral (que tem muito mais mensagem, logo responde depois)
      // sobrescrevia as mensagens da conversa que o usuário acabou de abrir.
      if (canalSolicitadoRef.current !== channelId) {
        console.log('⏭️ [CHAT] Resposta obsoleta do canal', channelId, '- atual:', canalSolicitadoRef.current);
        return;
      }

      if (response.data.success) {
        const msgs: Message[] = response.data.data.map((msg: any) => normaliseMessage(msg));
        const signature = buildSignature(msgs);

        if (signature !== lastMessagesSignature.current) {
          lastMessagesSignature.current = signature;
          setMessages(msgs);
          console.log(`✅ [CHAT] ${msgs.length} mensagens carregadas`);
        } else {
          console.log('⏭️ [CHAT] Mensagens inalteradas, evitando re-render desnecessário');
        }
      } else {
        setError(response.data.error || 'Erro ao carregar mensagens');
      }
    } catch (err: any) {
      console.error('❌ Erro ao carregar mensagens:', err);
      setError(err.response?.data?.error || 'Erro ao carregar mensagens');
    } finally {
      if (!options.silent) {
        setLoading(false);
      }
    }
  }, []);

  const sendMessage = useCallback(async (message: Omit<Message, 'id' | 'timestamp'>) => {
    try {
      console.log('📤 [CHAT] Enviando mensagem:', message);
      const response = await axios.post(`${API_URL}/api/chat/messages`, message, {
        timeout: 10000 // 10 segundos timeout
      });

      console.log('✅ [CHAT] Resposta do servidor:', response.data);

      if (response.data.success) {
        const newMessage = normaliseMessage(response.data.data);
        setMessages(prev => {
          const updated = [...prev, newMessage];
          lastMessagesSignature.current = buildSignature(updated);
          return updated;
        });
        console.log('✅ [CHAT] Mensagem adicionada ao estado local');
      } else {
        throw new Error(response.data.error || 'Erro ao enviar mensagem');
      }
    } catch (err: any) {
      console.error('❌ Erro ao enviar mensagem:', err);
      throw err;
    }
  }, []);

  const sendNotification = useCallback(async (channelId: string, content: string, metadata?: any) => {
    try {
      const response = await axios.post(`${API_URL}/api/chat/notifications`, {
        channelId,
        content,
        metadata
      });

      if (response.data.success) {
        const notification = {
          ...response.data.data,
          timestamp: new Date(response.data.data.timestamp)
        };
        setMessages(prev => [...prev, notification]);
      }
    } catch (err: any) {
      console.error('❌ Erro ao enviar notificação:', err);
    }
  }, []);

  const refreshMessages = useCallback(async (channelId: string, userId?: string, empresaId?: string) => {
    try {
      await loadMessages(channelId, { silent: true, userId, empresaId });
    } catch (error) {
      console.error('❌ [CHAT] Erro ao atualizar mensagens:', error);
      // Não propagar erro para não quebrar o polling
    }
  }, [loadMessages]);

  // Adicionar mensagem recebida via Socket.IO (sem chamar API)
  const addExternalMessage = useCallback((rawMsg: any) => {
    const msg = normaliseMessage(rawMsg);
    setMessages(prev => {
      // Evitar duplicata (pode já ter vindo do POST local)
      if (prev.some(m => m.id === msg.id || m._id === msg._id)) {
        console.log('⏭️ [CHAT] Mensagem duplicada ignorada:', msg.id);
        return prev;
      }
      const updated = [...prev, msg];
      lastMessagesSignature.current = buildSignature(updated);
      console.log('📩 [CHAT] Mensagem externa adicionada via Socket.IO:', msg.id);
      return updated;
    });
  }, []);

  return {
    channels,
    messages,
    loading,
    error,
    loadChannels,
    loadMessages,
    sendMessage,
    sendNotification,
    refreshMessages,
    addExternalMessage
  };
};
